import { AfterContentInit, Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { onboardingStorageKeys } from '../../constant'
import { Amplitude, Auth } from '../../injectable'
import { FormData, travelAgentType, SelfSignupRoles } from '../../module/shared-member/organism/login/login'
import { environment } from '../../../environments/environment'
import { AnnualBookingRevenue, FeaturedListingListingsGQL, PublicListing, Role } from '../../../graphql'
import { WhiteLabelService } from '../account/white-label-settings/white-label.service'

const queryParams = {
    user_exists: 'user_exists',
    error_message: 'error_message',
    success_message: 'success_message',
    signup: 'signup',
    email : 'email',
    firstName: 'firstName',
    lastName: 'lastName',
    invite_code: 'invite_code',
    invite_accepted: 'invite_accepted',
    role: 'role',
    asset: 'asset',
    self_signup: 'self_signup',
    travel_agent_type: 'travel_agent_type',
    company_name: 'company_name',
    consortium_name: 'consortium_name',
    consortium_membership_number: 'consortium_membership_number',
    booking_revenue: 'booking_revenue',
}

@Component({
    selector: 'app-onboarding',
    templateUrl: 'onboarding.html',
    styleUrls: ['onboarding.scss']
})
export class OnboardingComponent implements OnInit, AfterContentInit {

    publicListing: PublicListing
    featuredListingCode = environment.onboarding.featuredListingsId
    loading = true
    listingLoading = true
    mobileDrawerOpen = false
    activeTab = 1
    inviteCode: string | null
    inviteRole: string | null
    inviteAlreadyAccepted = false
    checkAuthenticationInProgress = true
    role: Role | undefined
    email: string | null | undefined
    formData: FormData = {}
    onloadMessage: any
    selfSignup: SelfSignupRoles | undefined
    SelfSignupRoles = SelfSignupRoles
    existingUser = false
    assetId: string
    isWhiteLabelSite = false

    constructor(
        private _amplitude: Amplitude,
        private _activatedRoute: ActivatedRoute,
        private _auth: Auth,
        private _fetchFeaturedListingGQL: FeaturedListingListingsGQL,
        private _router: Router,
        private _whiteLabelService: WhiteLabelService
    ) {

        if (this._auth.isAuthenticated()) {
            this._router.navigateByUrl('/')
            return
        }

        this.checkForSelfSignup()
        this.checkForWhiteLabelSite()
        this.checkForAcceptedInvitation()
        this.checkForAlreadyExistingUser()
        this.checkForOnloadMessage()
        this.checkForInvite()
        this.checkForEmail()
        this.checkForName()
        this.checkTravelAgentType()
        this.checkCompanyName()
        this.checkConsortiumName()
        this.checkConsortiumMembershipNumber()
        this.checkBookingRevenue()
        this.checkInitialiseForSignup()
        this.checkAuthenticationInProgress = false
    }

    ngOnInit(): void {
        this._fetchFeaturedListingGQL
            .fetch({ featuredListingCode: this.featuredListingCode }, { fetchPolicy: 'no-cache' })
            .subscribe(result => {
                if ( result.data?.featuredListings) {
                    this.publicListing = result.data?.featuredListings as PublicListing
                } else {
                    console.log('No feature listings')
                }
                setTimeout(() => {
                    this.listingLoading = false
                }, 100)
            })
    }

    ngAfterContentInit(): void {
        this.loading = false
    }

    private checkForAcceptedInvitation() {
        const inviteAlreadyAccepted = this._activatedRoute.snapshot.queryParams[queryParams.invite_accepted]

        if (inviteAlreadyAccepted && inviteAlreadyAccepted !== 'null') {
            this.inviteAlreadyAccepted = true
        }
    }

    private checkForAlreadyExistingUser() {
        const userExists = this._activatedRoute.snapshot.queryParams[queryParams.user_exists]

        if (userExists === 'true') {
            this.existingUser = true
        }
    }

    private checkForOnloadMessage() {
        const errorMessage = this._activatedRoute.snapshot.queryParams[queryParams.error_message]
        const successMessage = this._activatedRoute.snapshot.queryParams[queryParams.success_message]

        if (errorMessage) {
            this.onloadMessage = {
                message: errorMessage,
                type: 'error'
            }
        }
        else if (successMessage) {
            this.onloadMessage = {
                message: successMessage,
                type: 'success'
            }
        }
    }

    private checkInitialiseForSignup() {
        const signup = this._activatedRoute.snapshot.queryParams[queryParams.signup]

        if (signup) {
            this.activeTab = 2
        }
    }

    private checkForEmail() {
        const inviteeEmail = this._activatedRoute.snapshot.queryParams[queryParams.email]

        if (inviteeEmail) {
            this.formData.signupEmail = inviteeEmail
        }
    }

    private checkForName() {
        const firstName = this._activatedRoute.snapshot.queryParams[queryParams.firstName]
        const lastName = this._activatedRoute.snapshot.queryParams[queryParams.lastName]

        if (firstName && firstName !== 'null') {
            this.formData.signupFirstName = firstName
        }

        if (lastName && lastName !== 'null') {
            this.formData.signupLastName = lastName
        }
    }

    private checkTravelAgentType() {
        const travelAgentShorthand = this._activatedRoute.snapshot.queryParams[queryParams.travel_agent_type] ?? ''

        switch (travelAgentShorthand) {
            case 'agent':
                this.formData.signupTravelAgentType = travelAgentType.travelAgent
                break
            case 'operator':
                this.formData.signupTravelAgentType = travelAgentType.tourOperator
                break
            case 'designer':
                this.formData.signupTravelAgentType = travelAgentType.travelPlannerDesigner
                break
            default:
        }
    }

    private checkCompanyName() {
        const companyName = this._activatedRoute.snapshot.queryParams[queryParams.company_name]

        if (companyName) {
            this.formData.signupCompanyName = companyName
        }
    }
    private checkConsortiumName() {
        const consortiumName = this._activatedRoute.snapshot.queryParams[queryParams.consortium_name]

        if (consortiumName) {
            this.formData.signupConsortiumName = consortiumName
        }
    }
    private checkConsortiumMembershipNumber() {
        const consortiumMembershipNumber = this._activatedRoute.snapshot.queryParams[queryParams.consortium_membership_number]

        if (consortiumMembershipNumber) {
            this.formData.signupConsortiumMembershipNumber = consortiumMembershipNumber
        }

    }
    private checkBookingRevenue() {
        const bookingRevenueShorthand = this._activatedRoute.snapshot.queryParams[queryParams.booking_revenue] ?? ''

        switch (bookingRevenueShorthand) {
            case 'small':
                this.formData.signupBookingRevenue = AnnualBookingRevenue.LessThan_20000
                break
            case 'medium':
                this.formData.signupBookingRevenue = AnnualBookingRevenue.Between_20000And_500000
                break
            case 'large':
                this.formData.signupBookingRevenue = AnnualBookingRevenue.GreaterThan_500000
                break
            default:
        }

    }

    private checkForInvite() {
        // Store the values so that authentication page can redirect correctly
        this.inviteCode = this._activatedRoute.snapshot.queryParams[queryParams.invite_code]
        this.role = this._activatedRoute.snapshot.queryParams[queryParams.role]
        this.assetId = this._activatedRoute.snapshot.queryParams[queryParams.asset]
        this.mobileDrawerOpen = true

        if (this.inviteCode && this.role) {
            sessionStorage.setItem(onboardingStorageKeys.inviteCode, this.inviteCode)
            sessionStorage.setItem(onboardingStorageKeys.inviteRole, this.role)
            sessionStorage.setItem(onboardingStorageKeys.inviteAsset, this.assetId)
            this.activeTab = 2
        } else {
            // Always remove any previous codes on page load just as precaution
            sessionStorage.removeItem(onboardingStorageKeys.inviteCode)
            sessionStorage.removeItem(onboardingStorageKeys.inviteRole)
            sessionStorage.removeItem(onboardingStorageKeys.inviteAsset)
        }
    }

    private checkForSelfSignup() {
        this.selfSignup = this._activatedRoute.snapshot.queryParams[queryParams.self_signup]
    }

    private checkForWhiteLabelSite() {
        this.isWhiteLabelSite = this._whiteLabelService.isWhiteLabelEnabled()

        if (this.isWhiteLabelSite && this.selfSignup !== this.SelfSignupRoles.consumer) {
            window.location.href = `./onboarding?self_signup=${this.SelfSignupRoles.consumer}`
        } 
        else if (!this.isWhiteLabelSite && this.selfSignup === this.SelfSignupRoles.consumer) {
            window.location.href = `./onboarding?self_signup=${this.SelfSignupRoles.travel_agent}`
        }
    }

    openMobileDrawer(tabIndex: number) {
        this.activeTab = tabIndex
        this.mobileDrawerOpen = true
    }

    closeMobileDrawer() {
        this.mobileDrawerOpen = false
    }

    updateActiveTab(tabIndex: number){
        this.activeTab = tabIndex

        if (!this.role) {
            if (tabIndex === 1) {
                this._amplitude.logEventWithDebounce(100, 'User Login Tab Clicked')
            } else if (tabIndex === 2) {
                this._amplitude.logEventWithDebounce(100, 'User Signup Tab Clicked')
            }
        }
    }
}
